import React, { useState } from "react";
import toast from "react-hot-toast"; // Import toast
import emailjs from "emailjs-com";
import Button from "../button/Button";
import InputField from "../input-field/InputField";
import "./ContactForm.scss";

interface FormData {
  name: string;
  company: string;
  service: string;
  address: string;
  city: string;
  state: string;
  postalCode: string;
  phone: string;
  email: string;
  comments: string;
  [key: string]: string;
}

const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    company: "",
    service: "",
    address: "",
    city: "",
    state: "",
    postalCode: "",
    phone: "",
    email: "",
    comments: "",
  });

  const [isSending, setIsSending] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const sendEmail = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSending(true);

    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;

    if (!publicKey || !serviceId || !templateId) {
      setIsSending(false);
      toast.error(
        "Missing EmailJS configuration. Please check environment variables."
      );
      return;
    }

    emailjs.init(publicKey);

    emailjs
      .send(serviceId, templateId, formData, publicKey)
      .then((response) => {
        console.log("SUCCESS!", response.status, response.text);
        toast.success("Message sent successfully! We'll get back to you soon.");
        setFormData({
          name: "",
          company: "",
          service: "",
          address: "",
          city: "",
          state: "",
          postalCode: "",
          phone: "",
          email: "",
          comments: "",
        });
      })
      .catch((err) => {
        console.error("FAILED...", err);
        toast.error(
          `Failed to send message. ${err.text || "Please try again later."}`
        );
      })
      .finally(() => {
        setIsSending(false);
      });
  };

  return (
    <div className="contact-form">
      <h2 className="form-title">SEND US A MESSAGE!</h2>

      <form onSubmit={sendEmail}>
        <InputField
          type="text"
          placeholder="Name *"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required={true}
        />

        <InputField
          type="text"
          placeholder="Company"
          name="company"
          value={formData.company}
          onChange={handleChange}
        />

        <InputField
          type="text"
          placeholder="Service Interested in"
          name="service"
          value={formData.service}
          onChange={handleChange}
        />

        <InputField
          type="text"
          placeholder="Location / Address"
          name="address"
          value={formData.address}
          onChange={handleChange}
        />

        <div className="form-row">
          <InputField
            type="text"
            placeholder="City"
            name="city"
            value={formData.city}
            onChange={handleChange}
          />
          <InputField
            type="text"
            placeholder="State/Prov."
            name="state"
            value={formData.state}
            onChange={handleChange}
          />
        </div>

        <div className="form-row">
          <InputField
            type="text"
            placeholder="ZIP/Postal Code"
            name="postalCode"
            value={formData.postalCode}
            onChange={handleChange}
          />
          <InputField
            type="tel"
            placeholder="Phone Number"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
          />
        </div>

        <InputField
          type="email"
          placeholder="Email *"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required={true}
        />

        <div className="input-field">
          <textarea
            name="comments"
            value={formData.comments}
            onChange={handleChange}
            placeholder="Comments:"
            rows={4}
            className="input-element"
          />
        </div>

        <div className="form-footer">
          <Button
            text={isSending ? "SENDING..." : "SEND"}
            type="submit"
            className="submit-button"
            disabled={isSending}
          />
        </div>
      </form>
    </div>
  );
};

export default ContactForm;